<template>
  <component
    :is="as"
    ref="textRef"
    v-bind="forwardedAttrs"
    :class="['adaptive-fit-text', attrs.class]"
    :style="[attrs.style, visibleStyles]"
    :title="tooltipText"
  >
    <template v-for="(segment, index) in displaySegments" :key="`visible-${index}`">
      <span>{{ segment.text }}</span>
      <wbr v-if="segment.breakAfter" />
    </template>
  </component>
  <span
    ref="measureRef"
    aria-hidden="true"
    :class="['adaptive-fit-text', 'adaptive-fit-text--measure', attrs.class]"
    :style="[attrs.style, measureBaseStyles]"
  >
    <template v-for="(segment, index) in displaySegments" :key="`measure-${index}`">
      <span>{{ segment.text }}</span>
      <wbr v-if="segment.breakAfter" />
    </template>
  </span>
</template>

<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  useAttrs,
  watch,
} from "vue";

defineOptions({
  inheritAttrs: false,
});

type FitMode = "single" | "limited" | "free";
type DisplaySegment = {
  text: string;
  breakAfter: boolean;
};

const props = withDefaults(
  defineProps<{
    text?: string | number | null;
    as?: string;
    maxFontSize?: number;
    minFontSize?: number;
    maxLines?: number;
    allowMultiline?: boolean;
    tooltip?: string;
  }>(),
  {
    text: "",
    as: "span",
    maxFontSize: 16,
    minFontSize: 11,
    maxLines: 2,
    allowMultiline: true,
    tooltip: undefined,
  },
);

const attrs = useAttrs();
const textRef = ref<HTMLElement | null>(null);
const measureRef = ref<HTMLElement | null>(null);
const fontSizePx = ref(props.maxFontSize);
const lineHeightPx = ref(Math.round(props.maxFontSize * 1.2));
const whiteSpaceMode = ref<"nowrap" | "normal">("nowrap");
const overflowWrapMode = ref<"normal" | "anywhere">("normal");
const hyphenMode = ref<"manual" | "auto">("manual");

let resizeObserver: ResizeObserver | null = null;
let frameId = 0;

const displayText = computed(() => String(props.text ?? "").trim());

const isNaturalBreakChar = (char: string) =>
  char === "/" ||
  char === "\\" ||
  char === "," ||
  char === ";" ||
  char === ":" ||
  char === "|" ||
  char === "_" ||
  char === ")" ||
  char === "]" ||
  char === "}";

const displaySegments = computed<DisplaySegment[]>(() => {
  const source = displayText.value;

  if (!source) return [];

  const segments: DisplaySegment[] = [];
  let buffer = "";

  for (let index = 0; index < source.length; index += 1) {
    const char = source[index];
    const next = source[index + 1] ?? "";
    const prev = source[index - 1] ?? "";

    buffer += char;

    const shouldBreakAfter =
      isNaturalBreakChar(char) ||
      (char === "-" && next !== "" && next !== " ") ||
      (char === "(" && prev !== "" && prev !== " ") ||
      (char === " " && next !== "" && next !== " ");

    if (!shouldBreakAfter) continue;

    segments.push({
      text: buffer,
      breakAfter: char !== " ",
    });
    buffer = "";
  }

  if (buffer) {
    segments.push({
      text: buffer,
      breakAfter: false,
    });
  }

  return segments;
});

const tooltipText = computed(() => props.tooltip || displayText.value);

const forwardedAttrs = computed(() => {
  const {
    class: _class,
    style: _style,
    title: _title,
    ...rest
  } = attrs;

  return rest;
});

const lineHeightFor = (size: number, mode: FitMode) =>
  Math.round(size * (mode === "single" ? 1.15 : 1.2));

const commitMode = (mode: FitMode, size: number) => {
  const safeSize = Math.max(props.minFontSize, Math.min(props.maxFontSize, size));

  fontSizePx.value = safeSize;
  lineHeightPx.value = lineHeightFor(safeSize, mode);
  whiteSpaceMode.value = mode === "single" ? "nowrap" : "normal";
  overflowWrapMode.value = mode === "free" ? "anywhere" : "normal";
  hyphenMode.value = mode === "free" ? "auto" : "manual";
};

const visibleStyles = computed(() => ({
  display: "block",
  width: "100%",
  maxWidth: "100%",
  fontSize: `${fontSizePx.value}px`,
  lineHeight: `${lineHeightPx.value}px`,
  whiteSpace: whiteSpaceMode.value,
  overflowWrap: overflowWrapMode.value,
  wordBreak: "normal",
  textWrap: whiteSpaceMode.value === "nowrap" ? "nowrap" : "pretty",
  hyphens: hyphenMode.value,
}));

const measureBaseStyles = computed(() => ({
  display: "block",
}));

const getAvailableWidth = () => {
  const parentWidth = textRef.value?.parentElement?.getBoundingClientRect().width ?? 0;
  const ownWidth = textRef.value?.getBoundingClientRect().width ?? 0;

  return Math.max(Math.floor(parentWidth || ownWidth), 0);
};

const applyMeasureMode = (mode: FitMode, size: number, width: number) => {
  const el = measureRef.value;

  if (!el) return;

  const lineHeight = lineHeightFor(size, mode);

  el.style.fontSize = `${size}px`;
  el.style.lineHeight = `${lineHeight}px`;
  el.style.whiteSpace = mode === "single" ? "nowrap" : "normal";
  el.style.overflowWrap = mode === "free" ? "anywhere" : "normal";
  el.style.wordBreak = "normal";
  el.style.textWrap = mode === "single" ? "nowrap" : "pretty";
  el.style.hyphens = mode === "free" ? "auto" : "manual";
  el.style.width = mode === "single" ? "auto" : `${width}px`;
  el.style.maxWidth = mode === "single" ? "none" : `${width}px`;
};

const fitsSingleLine = (size: number, width: number) => {
  const el = measureRef.value;

  if (!el || width <= 0) return false;

  applyMeasureMode("single", size, width);
  return el.getBoundingClientRect().width <= width + 1;
};

const fitsLimitedLines = (size: number, width: number) => {
  const el = measureRef.value;

  if (!el || width <= 0) return false;

  applyMeasureMode("limited", size, width);
  const maxHeight = lineHeightFor(size, "limited") * props.maxLines + 1;

  return el.scrollHeight <= maxHeight;
};

const findBestFit = (
  minSize: number,
  maxSize: number,
  predicate: (size: number) => boolean,
) => {
  let low = minSize;
  let high = maxSize;
  let best = 0;

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);

    if (predicate(mid)) {
      best = mid;
      low = mid + 1;
      continue;
    }

    high = mid - 1;
  }

  return best;
};

const recalculate = () => {
  if (!textRef.value || !measureRef.value) return;

  if (!displayText.value) {
    commitMode("single", props.maxFontSize);
    return;
  }

  const availableWidth = getAvailableWidth();

  if (availableWidth <= 0) {
    commitMode("single", props.maxFontSize);
    return;
  }

  const minSize = Math.min(props.minFontSize, props.maxFontSize);
  const maxSize = Math.max(props.minFontSize, props.maxFontSize);

  if (fitsSingleLine(maxSize, availableWidth)) {
    commitMode("single", maxSize);
    return;
  }

  if (props.allowMultiline && props.maxLines > 1) {
    if (fitsLimitedLines(maxSize, availableWidth)) {
      commitMode("limited", maxSize);
      return;
    }

    const limitedLinesSize = findBestFit(minSize, maxSize, (size) =>
      fitsLimitedLines(size, availableWidth),
    );

    if (limitedLinesSize >= minSize) {
      commitMode("limited", limitedLinesSize);
      return;
    }
  }

  const singleLineSize = findBestFit(minSize, maxSize, (size) =>
    fitsSingleLine(size, availableWidth),
  );

  if (singleLineSize >= minSize) {
    commitMode("single", singleLineSize);
    return;
  }

  commitMode("free", minSize);
};

const scheduleRecalculate = async () => {
  if (typeof window === "undefined") return;

  await nextTick();
  window.cancelAnimationFrame(frameId);
  frameId = window.requestAnimationFrame(recalculate);
};

onMounted(() => {
  scheduleRecalculate();

  if (typeof ResizeObserver !== "undefined" && textRef.value?.parentElement) {
    resizeObserver = new ResizeObserver(() => {
      scheduleRecalculate();
    });

    resizeObserver.observe(textRef.value.parentElement);
  }

  document.fonts?.ready.then(() => {
    scheduleRecalculate();
  });
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  resizeObserver = null;

  if (typeof window !== "undefined") {
    window.cancelAnimationFrame(frameId);
  }
});

watch(
  () => [
    displayText.value,
    props.maxFontSize,
    props.minFontSize,
    props.maxLines,
    props.allowMultiline,
  ],
  () => {
    scheduleRecalculate();
  },
);
</script>

<style scoped>
.adaptive-fit-text {
  min-width: 0;
  max-width: 100%;
  white-space: normal;
  word-break: normal;
  overflow-wrap: normal;
  text-wrap: pretty;
  hyphens: manual;
  vertical-align: top;
}

.adaptive-fit-text--measure {
  position: fixed;
  top: -9999px;
  left: -9999px;
  z-index: -1;
  visibility: hidden;
  pointer-events: none;
}
</style>
